import { Box, TextField, InputAdornment, CircularProgress } from '@mui/material';
import React, { FC, ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import { ATTHERATEICON, CopyClipBoardIcon, CopyClipBoardSuccessIcon } from '../Icons';
import CustomButton from '../CustomButton/CustomButton';

interface StyledTextFieldProps {
	error?: boolean;
}

interface StyledTextFieldProps extends React.HTMLProps<HTMLInputElement> {
	error?: boolean;
	styles?: React.CSSProperties;
	type?: string; // Add a type prop to determine the input type
}

const StyledTextFieldContainer = styled(TextField)<StyledTextFieldProps>`
	input,
	textarea {
		padding: 10px;
		${(props) => `
      width: ${props?.styles?.width || '100%'};
      height: ${props?.styles?.height || '100%'};
    `}
		border: 1.5px solid ${(props) => (props?.error ? 'red' : '#e9e9e9')};
		border-radius: 4px;
		padding-left: 10px;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		background: #fff;

		&:focus {
			border: 1.5px solid ${(props) => (props?.error ? 'red' : '#acacac')};
		}
	}
	.icon {
		svg {
			position: absolute;
			right: 9%;
		}
	}
`;

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
	span {
		color: #f26184;
		margin-left: 4px;
	}
`;

const Wrapper = styled.div`
	position: relative;
`;

const StatusMessage = styled.div({
	fontSize: '13px',
	fontWeight: 600,
	lineHeight: '15.6px',
	marginRight: '40px',
});

interface LabeledTextFieldProps {
	label?: string;
	error?: boolean;
	onChange: any;
	value: string;
	placeholder?: string;
	styles?: React.CSSProperties;
	disabled?: boolean;
	multiline?: boolean;
	rows?: number;
	maxRows?: number;
	required?: boolean;
	readOnly?: boolean;
	type?: string;
	starsLength?: number;
	showPassword?: boolean;
	allowCopy?: boolean;
	marginLeft?: string;
	icon?: boolean;
	statusMessage?: React.ReactNode;
	buttonDisableState?: boolean;
	buttonOnclick?: any;
	isBtnLoading?: any;
	autoFocus?: boolean;
	name?: string;
	errorText?: string;
}

const LabeledTextField: FC<LabeledTextFieldProps> = ({
	label,
	error,
	value,
	onChange,
	placeholder,
	styles = { width: '344px', height: '36px' },
	disabled = false,
	multiline = false,
	rows = 4,
	maxRows = 8,
	required = false,
	type,
	readOnly = false,
	showPassword = false,
	starsLength = 14,
	allowCopy = false,
	marginLeft = '0px',
	icon = true,
	statusMessage,
	buttonDisableState,
	buttonOnclick,
	isBtnLoading,
	autoFocus = false,
	errorText = '',
	...props
}) => {
	const [copyClipboard, setCopyClipBoard] = useState(false);

	const successIconDisplay = () => {
		setCopyClipBoard(true); // Set the state to show the success icon

		setTimeout(() => {
			setCopyClipBoard(false); // Reset the state after a certain duration (e.g., 2 seconds)
		}, 2000);
	};

	const copyToClipBoard = (text: string) => {
		if (!navigator.clipboard) {
			// Clipboard API not available, fallback to a different method (e.g., execCommand)
			const textarea = document.createElement('textarea');
			textarea.value = text;
			textarea.style.position = 'fixed'; // Prevent scrolling to bottom
			document.body.appendChild(textarea);
			textarea.focus();
			textarea.select();

			try {
				const successful = document.execCommand('copy');
				const message = successful ? 'Copied to clipboard' : 'Unable to copy';
			} catch (err) {
				console.error('Unable to copy', err);
			}

			document.body.removeChild(textarea);

			return;
		}

		// Use Clipboard API if available
		navigator.clipboard
			.writeText(text)
			.then(() => {
				successIconDisplay();
			})
			.catch((err) => {
				console.error('Unable to copy', err);
			});
	};

	const copyClipBoardIcon = (value: string) => {
		return (
			<InputAdornment
				className="icon"
				sx={{ cursor: 'pointer' }}
				position="end"
				onClick={() => copyToClipBoard(value)}
			>
				{copyClipboard ? <CopyClipBoardSuccessIcon /> : <CopyClipBoardIcon />}
			</InputAdornment>
		);
	};

	const renderInput = () => {
		if (type === 'email' && icon) {
			return (
				<StyledTextFieldContainer
					variant="standard"
					InputProps={{
						disableUnderline: true,
						classes: {
							root: 'MuiInputBase-root',
						},
						endAdornment: (
							<InputAdornment className="icon" position="end">
								<ATTHERATEICON />
							</InputAdornment>
						),
					}}
					multiline={multiline}
					rows={rows}
					maxRows={maxRows}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					styles={styles}
					disabled={disabled}
					{...props}
					error={error}
					type={type}
					inputProps={{ readOnly: readOnly }}
					autoFocus={autoFocus}
				/>
			);
		} else if (type === 'phoneNumber') {
			const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
				const phoneNumberRegex = /^[0-9]*$/;
				const { value: inputValue } = event.target;

				if (phoneNumberRegex.test(inputValue) || inputValue === '') {
					onChange(event);
				}
			};

			return (
				<StyledTextFieldContainer
					variant="standard"
					InputProps={{
						disableUnderline: true,
						classes: {
							root: 'MuiInputBase-root',
						},
					}}
					multiline={multiline}
					rows={rows}
					maxRows={maxRows}
					value={value}
					onChange={handlePhoneNumberChange}
					placeholder={placeholder}
					styles={styles}
					disabled={disabled}
					{...props}
					error={error}
					type="tel"
					inputProps={{ readOnly: readOnly }}
					autoFocus={autoFocus}
				/>
			);
		} else if (type === 'password') {
			const inputValue = showPassword ? value : '*'?.repeat(starsLength);

			return (
				<StyledTextFieldContainer
					variant="standard"
					InputProps={{
						disableUnderline: true,
						classes: {
							root: 'MuiInputBase-root',
						},
						endAdornment: allowCopy && value?.length ? copyClipBoardIcon(value) : null,
					}}
					onSelect={allowCopy && value?.length ? () => copyToClipBoard(value) : () => {}}
					sx={
						readOnly
							? {
									input: {
										cursor: 'pointer',
									},
							  }
							: null
					}
					multiline={multiline}
					rows={rows}
					maxRows={maxRows}
					value={inputValue}
					onChange={onChange}
					placeholder={placeholder}
					styles={styles}
					disabled={disabled}
					{...props}
					error={error}
					type="text"
					inputProps={{ readOnly: readOnly }}
					autoFocus={autoFocus}
				/>
			);
		} else if (type === 'text' && statusMessage) {
			return (
				<Wrapper style={styles}>
					<StyledTextFieldContainer
						variant="standard"
						InputProps={{
							disableUnderline: true,
							classes: {
								root: 'MuiInputBase-root',
							},
							endAdornment: allowCopy && value?.length ? copyClipBoardIcon(value) : null,
						}}
						onSelect={allowCopy && value?.length ? () => copyToClipBoard(value) : () => {}}
						sx={
							readOnly
								? {
										input: {
											cursor: 'pointer',
										},
								  }
								: null
						}
						multiline={multiline}
						rows={rows}
						maxRows={maxRows}
						value={value}
						onChange={onChange}
						placeholder={placeholder}
						styles={styles}
						disabled={disabled}
						{...props}
						error={error}
						type={type}
						inputProps={{ readOnly: readOnly }}
						autoFocus={autoFocus}
					/>
					<CustomButton
						title={
							isBtnLoading ? (
								<CircularProgress size={10} style={{ color: 'var(--default-text-color)' }} />
							) : (
								'Check Availability'
							)
						}
						color="primary"
						styles={{
							height: '22px',
							width: '142px',
							position: 'absolute',
							padding: '10px',
							top: '7px',
							right: '10px',
							fontSize: '13px',
							fontWeight: 400,
						}}
						handleClick={buttonOnclick}
						disabled={buttonDisableState || isBtnLoading}
					/>
					{/* <StatusMessage>{statusMessage}</StatusMessage> */}
				</Wrapper>
			);
		} else {
			return (
				<StyledTextFieldContainer
					variant="standard"
					InputProps={{
						disableUnderline: true,
						classes: {
							root: 'MuiInputBase-root',
						},
						endAdornment: allowCopy && value?.length ? copyClipBoardIcon(value) : null,
					}}
					onSelect={allowCopy && value?.length ? () => copyToClipBoard(value) : () => {}}
					sx={
						readOnly
							? {
									input: {
										cursor: 'pointer',
									},
							  }
							: null
					}
					multiline={multiline}
					rows={rows}
					maxRows={maxRows}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					styles={styles}
					disabled={disabled}
					{...props}
					error={error}
					type={type}
					inputProps={{ readOnly: readOnly }}
					autoFocus={autoFocus}
				/>
			);
		}
	};

	return (
		<Box display="flex" flexDirection="column" gap="5px" marginLeft={marginLeft} width={'100%'}>
			{statusMessage ? (
				<div style={{ display: 'flex', justifyContent: 'space-between' }}>
					<StyledLabel style={disabled ? { color: 'var(--gray-g-400, #ACACAC)' } : {}}>
						{label}
						{required ? <span>*</span> : null}
					</StyledLabel>
					<StatusMessage>{statusMessage}</StatusMessage>
				</div>
			) : (
				<StyledLabel style={disabled ? { color: 'var(--gray-g-400, #ACACAC)' } : {}}>
					{label}
					{required ? <span>*</span> : null}
				</StyledLabel>
			)}
			{renderInput()}
			{errorText && errorText !== '' && (
				<div style={{ color: 'red', fontSize: '12px' }}>{errorText}</div>
			)}
		</Box>
	);
};

export default LabeledTextField;
